import { useState } from 'react';
import { useLocation, Link } from 'react-router';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import FolderIcon from '@mui/icons-material/Folder';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useAuth } from '../../auth';
import { ROUTES } from '../../constants';

export const SIDEBAR_WIDTH = 220;
export const SIDEBAR_COLLAPSED_WIDTH = 56;

export function Sidebar() {
    const { pathname } = useLocation();
    const { user } = useAuth();
    const isAdmin = user?.roles.includes('admin');
    const [collapsed, setCollapsed] = useState(false);

    const width = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;

    const navItems = [
        {
            label: 'Projects',
            icon: <FolderIcon />,
            to: ROUTES.PROJECTS,
            selected: pathname === ROUTES.PROJECTS || pathname.startsWith('/projects'),
            show: true,
        },
        {
            label: 'Admin',
            icon: <AdminPanelSettingsIcon />,
            to: ROUTES.ADMIN_CUSTOMERS,
            selected: pathname.startsWith('/admin'),
            show: isAdmin,
        },
    ];

    return (
        <Box
            component="nav"
            sx={{
                width,
                flexShrink: 0,
                borderRight: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflow: 'hidden',
                transition: 'width 0.2s ease',
            }}
        >
            <List dense sx={{ px: collapsed ? 0.5 : 1, pt: 1, flex: 1 }}>
                {navItems
                    .filter((item) => item.show)
                    .map((item) => (
                        <Tooltip key={item.label} title={collapsed ? item.label : ''} placement="right">
                            <ListItemButton
                                component={Link}
                                to={item.to}
                                selected={item.selected}
                                sx={{
                                    borderRadius: 1,
                                    mb: 0.5,
                                    justifyContent: collapsed ? 'center' : 'flex-start',
                                    px: collapsed ? 1 : 2,
                                    minHeight: 40,
                                }}
                            >
                                <ListItemIcon
                                    sx={{
                                        minWidth: collapsed ? 0 : 36,
                                        justifyContent: 'center',
                                    }}
                                >
                                    {item.icon}
                                </ListItemIcon>
                                {!collapsed && <ListItemText primary={item.label} />}
                            </ListItemButton>
                        </Tooltip>
                    ))}
            </List>

            <Box
                sx={{
                    borderTop: 1,
                    borderColor: 'divider',
                    display: 'flex',
                    justifyContent: collapsed ? 'center' : 'flex-end',
                    p: 0.5,
                }}
            >
                <IconButton
                    size="small"
                    onClick={() => {
                        setCollapsed((c) => !c);
                    }}
                    aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                >
                    {collapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
                </IconButton>
            </Box>
        </Box>
    );
}
